import { Component, OnDestroy, OnInit } from '@angular/core';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Subject, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment-timezone';
import { LevelOfServiceService } from '../../level-of-service/service/level-of-service.service';
import { ZonesService } from '../../zones/zones-service/zones.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { AvailabilityMatrixService } from '../availability-matrix.service';
import { ScheduleHour } from '../availability-matrix.component';


@Component({
  selector: 'app-availability-coverage',
  templateUrl: './availability-coverage.component.html',
  styleUrls: ['./availability-coverage.component.css']
})
export class AvailabilityCoverageComponent implements OnInit, OnDestroy {
  timeZone = 'America/New_York';
  selectedLos: string | null = null;
  selectedZoneId: string | null = null;
  coverageByHour: { [hour: number]: number } = {};

  levelOfServices: any[] = [];
  zones: any[] = [];
  zoneMap: { [id: string]: any } = {};
  hours: ScheduleHour[] = [];

  selectedDate: any = null;
  weekLabel = '';
  currentWeekStart: any = null;
  currentWeekEnd: any = null;

  selectedZone: any = null;
  selectedPath: any[] = [];
  isLoading = false;
  mapOptions = {
    lat: 33.5362475,
    lng: -111.9267386,
    zoom: 10
  };
  saveOverlayVisible: { [hour: number]: boolean } = {};
  saveOverlayActive: { [hour: number]: boolean } = {};
  private agmMap: any;
  private coverageLoadToken = 0;
  private savedCoverageByHour: { [hour: number]: number } = {};
  private coverageSave$ = new Subject<void>();
  private pendingSaves: { [key: string]: { hour: number; value: number; filters: any; startDate: string; endDate: string } } = {};
  private saveUiTokens: { [hour: number]: number } = {};
  private readonly coverageSaveDebounceMs = 200;
  private readonly saveFadeOutMs = 100;

  constructor(
    private sharedDataService: SharedDataService,
    private availabilityMatrixService: AvailabilityMatrixService,
    private levelOfServiceService: LevelOfServiceService,
    private zonesService: ZonesService,
    private toastr: ToastrService
  ) {
    this.hours = Array.from({ length: 24 }, (_, h) => {
      const paddedHour = h < 10 ? '0' + h : '' + h;
      return {
        hour: h,
        label: paddedHour + ':00'
      };
    });
    this.coverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
    this.savedCoverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
  }

  ngOnInit() {
    this.coverageSave$
      .pipe(debounceTime(this.coverageSaveDebounceMs), takeUntil(componentDestroyed(this)))
      .subscribe(() => {
        const pending = this.pendingSaves;
        this.pendingSaves = {};
        Object.keys(pending).forEach((key) => {
          this.saveCoverage(pending[key]);
        });
      });

    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }
        if (data.timeZone) {
          this.timeZone = data.timeZone;
        }
        const startOfWeek = moment().tz(this.timeZone).startOf('isoWeek');
        const endOfWeek = moment().tz(this.timeZone).endOf('isoWeek');
        this.currentWeekStart = startOfWeek;
        this.currentWeekEnd = endOfWeek;
        this.weekLabel = startOfWeek.format('MM/DD/YYYY') + ' ~ ' + endOfWeek.format('MM/DD/YYYY');
        this.selectedDate = [startOfWeek.toDate(), endOfWeek.toDate()];

        this.levelOfServiceService
          .getLevelOfServices({ status: 'active' })
          .pipe(takeUntil(componentDestroyed(this)))
          .subscribe(
            (losData: any) => {
              this.levelOfServices = losData || [];
            },
            () => {
              this.levelOfServices = [];
            }
          );

        this.zonesService
          .getZones({ status: 'active' })
          .pipe(takeUntil(componentDestroyed(this)))
          .subscribe(
            (zoneData: any) => {
              this.zones = zoneData || [];
              this.zoneMap = this.zones.reduce((obj, item) => (obj[item._id] = item, obj), {});
            },
            () => {
              this.zones = [];
              this.zoneMap = {};
            }
          );
      });
  }

  ngOnDestroy() {
  }

  onPickerClosed() {
    if (!this.selectedDate) {
      return;
    }
    let picked: any;
    if (Array.isArray(this.selectedDate)) {
      picked = this.selectedDate[0] || this.selectedDate[1];
    } else {
      picked = this.selectedDate;
    }
    if (!picked) {
      return;
    }
    const pickedMoment = moment(picked).tz(this.timeZone);
    const startOfWeek = moment(pickedMoment).startOf('isoWeek');
    const endOfWeek = moment(pickedMoment).endOf('isoWeek');
    const currentStart = this.currentWeekStart ? this.currentWeekStart.format('YYYY-MM-DD') : '';
    this.currentWeekStart = startOfWeek;
    this.currentWeekEnd = endOfWeek;
    this.weekLabel = startOfWeek.format('MM/DD/YYYY') + ' ~ ' + endOfWeek.format('MM/DD/YYYY');
    this.selectedDate = [startOfWeek.toDate(), endOfWeek.toDate()];
    if (startOfWeek.format('YYYY-MM-DD') === currentStart) {
      return;
    }
    this.loadCoverage();
  }

  loadCoverage() {
    const token = ++this.coverageLoadToken;
    this.coverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
    this.savedCoverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
    this.saveOverlayVisible = {};
    this.saveOverlayActive = {};
    if (!this.selectedLos || !this.selectedZoneId || !this.currentWeekStart || !this.currentWeekEnd) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.availabilityMatrixService
      .getCoverage({
        startDate: this.currentWeekStart.toISOString(),
        endDate: this.currentWeekEnd.toISOString(),
        filters: {
          los: this.selectedLos,
          zone: this.selectedZoneId
        }
      })
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data: any) => {
          if (token !== this.coverageLoadToken) {
            return;
          }
          this.isLoading = false;
          const hours = data && data.hours ? data.hours : (Array.isArray(data) ? data : []);
          this.coverageByHour = hours.reduce((obj:any, row:any) => {
            const hour = parseInt((row.hour + '').split(':')[0], 10);
            if (!isNaN(hour) && hour >= 0 && hour <= 23) {
              const parsed = Number(row.value);
              obj[hour] = isNaN(parsed) || parsed < 0 ? 0 : parsed;
            }
            return obj;
          }, this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {}));
          this.savedCoverageByHour = { ...this.coverageByHour };
        },
        (err) => {
          if (token !== this.coverageLoadToken) {
            return;
          }
          this.isLoading = false;
          this.coverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
          this.savedCoverageByHour = this.hours.reduce((obj:any, item:any) => (obj[item.hour] = 0, obj), {});
          this.toastr.error(err || 'Unable to load coverage', 'Error');
        }
      );
  }

  onZoneChange() {
    this.selectedZone = this.selectedZoneId ? this.zoneMap[this.selectedZoneId] : null;
    this.selectedPath = [];
    if (this.selectedZone && this.selectedZone.geo && this.selectedZone.geo.coordinates && this.selectedZone.geo.coordinates[0]) {
      this.selectedPath = this.selectedZone.geo.coordinates[0].map((element:any) => ({ lat: element[1], lng: element[0] }));
      if (this.selectedPath.length) {
        this.mapOptions = {
          lat: this.selectedPath[0].lat,
          lng: this.selectedPath[0].lng,
          zoom: 11
        };
      }
      setTimeout(() => {
        const googleRef = (window as any).google;
        if (!this.agmMap || !this.selectedPath.length || !googleRef || !googleRef.maps) {
          return;
        }
        const bounds = new googleRef.maps.LatLngBounds();
        this.selectedPath.forEach((point) => {
          bounds.extend(new googleRef.maps.LatLng(point.lat, point.lng));
        });
        this.agmMap.fitBounds(bounds);
      }, 0);
    }
    this.loadCoverage();
  }

  stepCoverage(hour: number, delta: number, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.selectedLos || !this.selectedZoneId || this.isLoading || !this.currentWeekStart || !this.currentWeekEnd) {
      return;
    }
    let next = (this.coverageByHour[hour] || 0) + delta;
    if (next < 0) {
      next = 0;
    } else if (next > 9999) {
      next = 9999;
    }
    this.coverageByHour = { ...this.coverageByHour, [hour]: next };
    const startDate = this.currentWeekStart.toISOString();
    const endDate = this.currentWeekEnd.toISOString();
    const filters = {
      los: this.selectedLos,
      zone: this.selectedZoneId
    };
    this.pendingSaves[filters.los + '|' + filters.zone + '|' + startDate + '|' + endDate + '|' + hour] = {
      hour,
      value: next,
      filters,
      startDate,
      endDate
    };
    this.coverageSave$.next();
  }

  saveCoverage(payload: { hour: number; value: number; filters: any; startDate: string; endDate: string }) {
    if (!payload || !payload.filters || !payload.filters.los || !payload.filters.zone || !payload.startDate || !payload.endDate) {
      return;
    }
    const value = payload.value < 0 ? 0 : payload.value;
    const currentStartDate = this.currentWeekStart ? this.currentWeekStart.toISOString() : '';
    const currentEndDate = this.currentWeekEnd ? this.currentWeekEnd.toISOString() : '';
    const isCurrent = payload.filters.los === this.selectedLos &&
      payload.filters.zone === this.selectedZoneId &&
      payload.startDate === currentStartDate &&
      payload.endDate === currentEndDate;
    if (isCurrent && (this.savedCoverageByHour[payload.hour] || 0) === value) {
      return;
    }
    const previousValue = isCurrent ? (this.savedCoverageByHour[payload.hour] || 0) : value;
    if (isCurrent) {
      this.savedCoverageByHour = { ...this.savedCoverageByHour, [payload.hour]: value };
    }
    let token = 0;
    if (isCurrent) {
      token = (this.saveUiTokens[payload.hour] || 0) + 1;
      this.saveUiTokens[payload.hour] = token;
      this.saveOverlayVisible = { ...this.saveOverlayVisible, [payload.hour]: true };
      this.saveOverlayActive = { ...this.saveOverlayActive, [payload.hour]: false };
      setTimeout(() => {
        if (this.saveUiTokens[payload.hour] !== token) {
          return;
        }
        this.saveOverlayActive = { ...this.saveOverlayActive, [payload.hour]: true };
      }, 0);
    }
    this.availabilityMatrixService
      .saveCoverage({
        filters: payload.filters,
        startDate: payload.startDate,
        endDate: payload.endDate,
        items: {
          hour: this.hours[payload.hour].label,
          value
        }
      })
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        () => {
          if (token && this.saveUiTokens[payload.hour] === token) {
            this.saveOverlayActive = { ...this.saveOverlayActive, [payload.hour]: false };
            timer(this.saveFadeOutMs)
              .pipe(takeUntil(componentDestroyed(this)))
              .subscribe(() => {
                if (this.saveUiTokens[payload.hour] !== token) {
                  return;
                }
                const visible = { ...this.saveOverlayVisible };
                delete visible[payload.hour];
                this.saveOverlayVisible = visible;
              });
          }
        },
        (err) => {
          const stillCurrent = payload.filters.los === this.selectedLos &&
            payload.filters.zone === this.selectedZoneId &&
            payload.startDate === (this.currentWeekStart ? this.currentWeekStart.toISOString() : '') &&
            payload.endDate === (this.currentWeekEnd ? this.currentWeekEnd.toISOString() : '');
          if (stillCurrent) {
            this.coverageByHour = { ...this.coverageByHour, [payload.hour]: previousValue };
            this.savedCoverageByHour = { ...this.savedCoverageByHour, [payload.hour]: previousValue };
          }
          if (token && this.saveUiTokens[payload.hour] === token) {
            this.saveOverlayActive = { ...this.saveOverlayActive, [payload.hour]: false };
            timer(this.saveFadeOutMs)
              .pipe(takeUntil(componentDestroyed(this)))
              .subscribe(() => {
                if (this.saveUiTokens[payload.hour] !== token) {
                  return;
                }
                const visible = { ...this.saveOverlayVisible };
                delete visible[payload.hour];
                this.saveOverlayVisible = visible;
              });
          }
          this.toastr.error(err || 'Unable to save coverage', 'Error');
        }
      );
  }

  onMapReady(map: any) {
    this.agmMap = map;
    const googleRef = (window as any).google;
    if (!this.agmMap || !this.selectedPath.length || !googleRef || !googleRef.maps) {
      return;
    }
    const bounds = new googleRef.maps.LatLngBounds();
    this.selectedPath.forEach((point) => {
      bounds.extend(new googleRef.maps.LatLng(point.lat, point.lng));
    });
    this.agmMap.fitBounds(bounds);
  }
}
